import React, { useState } from 'react';
import { Upload, Button, Typography, Card, message, Divider, Alert } from 'antd';
import { UploadOutlined, InboxOutlined, FileExcelOutlined, FileImageOutlined, DownloadOutlined } from '@ant-design/icons';
import { Backdrop, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { BASE_URL } from '../../App';

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

export default function ExcelUpload() {
    const [spinning, setSpinning] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [UploadExcelFile, setUploadExcelFile] = useState([]);
    const [UploadImages, setUploadImages] = useState([]);
    const login_id = localStorage.getItem("LOGIN_ID");

    const AlertMsg = (type, msg, duration) => {
        messageApi.open({
            type: type,
            content: msg,
            duration: duration,
        });
    };

    const ExcelUpload = {
        onRemove: (file) => {
            const newFileList = UploadExcelFile.filter((f) => f !== file);
            setUploadExcelFile(newFileList);
            AlertMsg("info", "File removed successfully", 2);
        },
        beforeUpload: (file) => {
            setUploadExcelFile([file]);
            AlertMsg("success", "File ready to upload!", 2);
            return false;
        },
        fileList: UploadExcelFile,
    };

    const ImgUpload = {
        onRemove: (file) => {
            const newFileList = UploadImages.filter((f) => f.uid !== file.uid);
            setUploadImages(newFileList);
            AlertMsg("info", "Image removed successfully", 2);
        },
        beforeUpload: (file) => {
            setUploadImages(prev => [...prev, file]);
            AlertMsg("success", `${file.name} ready to upload!`, 2);
            return false;
        },
        fileList: UploadImages,
    };

    const SubmitBtnClick = () => {
        setSpinning(true);

        if (UploadImages.length === 0) {
            AlertMsg("error", "Please upload at least one image.", 3);
            setSpinning(false);
            return;
        }

        if (UploadExcelFile.length === 0) {
            AlertMsg("error", "Please upload an Excel file.", 3);
            setSpinning(false);
            return;
        }

        const formData = new FormData();
        UploadExcelFile.forEach((file) => {
            formData.append('ExcelFile', file);
        });
        UploadImages.forEach((file) => {
            formData.append('ImgUpload[]', file);
        });
        formData.append('LOGIN_ID', login_id);
        formData.append('type', 'ExcelMaterialImageMapping');

        axios.post(BASE_URL + '/siso_uploads', formData)
            .then(response => {
                Swal.fire({
                    icon: "success",
                    text: "Excel Uploaded Successfully!",
                });
                setUploadExcelFile([]); // Clear Excel
                setUploadImages([]);    // Clear all images
            })
            .catch(error => {
                Swal.fire({
                    icon: "warning",
                    title: error.response?.data?.message || 'Upload Failed',
                    html: error.response?.data?.val?.replace(/,/g, '<br>') || '',
                    willOpen: () => {
                        const titleElement = Swal.getTitle();
                        titleElement.style.fontSize = '16px';
                        titleElement.style.fontWeight = 'bold';

                        const popupElement = Swal.getPopup();
                        const textElement = popupElement.querySelector('.swal2-html-container');
                        if (textElement) {
                            textElement.style.fontSize = '13px';
                            textElement.style.fontWeight = 'bold';
                        }
                    }
                });
            })
            .finally(() => {
                setSpinning(false);
            });
    };

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={spinning} >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Card
                style={{
                    borderRadius: '18px',
                    padding: '30px',
                    background: 'linear-gradient(to bottom, #ffffff, #e6f7ff)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
                }}
                bordered={false}
            >
                {contextHolder}
                <Title level={3} style={{ textAlign: 'center', color: '#1677ff' }}>
                    <FileExcelOutlined /> Excel & Image Mapping Upload
                </Title>


                <Alert
                    message="NOTE"
                    description={
                        <div>
                            <ul style={{ paddingLeft: '20px' }}>
                                <li><Text type="secondary">Upload your Excel file and related images. The system will automatically map <Text type="secondary"><b>Material Codes</b></Text> inside the Excel with the corresponding uploaded images.</Text></li>
                                <li><Text type="secondary">Allowed Excel format: <b>.xls / .xlsx</b></Text></li>
                                <li><Text type="secondary">Allowed image format: <b>.png / .jpg / .jpeg</b></Text></li>
                            </ul>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 30 }}
                />

                <Divider orientation="center" style={{ borderColor: '#1677ff' }}>
                    <FileImageOutlined /> Upload Images
                </Divider>

                <center style={{ marginBottom: 30 }}>
                    <Upload
                        {...ImgUpload}
                        listType="picture"
                        multiple
                        showUploadList
                    >
                        <Button type="primary" icon={<UploadOutlined />}>Select & Upload Images</Button>
                    </Upload>
                </center>

                <Divider orientation="center" style={{ borderColor: '#1677ff' }}>
                    <FileExcelOutlined /> Upload Excel File
                </Divider>


                {/* Download Button */}
                <Button
                    style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        color: '#fff',
                        marginBottom: '20px',
                        float: 'right',
                        fontWeight: 'bold',
                        borderRadius: '5px'
                    }}
                    icon={<DownloadOutlined />}
                >
                    <a href="./Siso_ItemCodes.xlsx" download style={{ color: 'inherit' }} >
                        Download Sample Excel
                    </a>
                </Button>

                <Dragger {...ExcelUpload} style={{ borderRadius: '10px', clear: 'both' }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#1677ff' }} />
                    </p>
                    <p className="ant-upload-text">Click or drag Excel file here</p>
                    <p className="ant-upload-hint">
                        Upload a single Excel file with Material Codes to map against uploaded images.
                    </p>
                </Dragger>

                <Button
                    type="primary"
                    style={{
                        marginTop: '25px',
                        width: '100%',
                        borderRadius: '30px',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        background: 'linear-gradient(90deg, #1677ff 0%, #69b1ff 100%)',
                        boxShadow: '0 6px 15px rgba(22, 119, 255, 0.3)',
                    }}
                    onClick={SubmitBtnClick}
                >
                    Submit
                </Button>
            </Card>
        </>
    );
}
